import { useEffect, useState } from 'react';
import type { Cotacao } from '@/db';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface RecentTableProps {
  cotacoes: Cotacao[];
  canSeeSensitive: boolean;
}

export default function RecentTable({ cotacoes, canSeeSensitive }: RecentTableProps) {
  const [pagina, setPagina] = useState(1);
  const porPagina = 10;
  const totalPaginas = Math.max(1, Math.ceil(cotacoes.length / porPagina));
  const inicio = (pagina - 1) * porPagina;
  const resultados = cotacoes.slice(inicio, inicio + porPagina);
  useEffect(() => setPagina(1), [cotacoes]);
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
        <h3 className="font-heading text-base font-semibold text-foreground-950">
          Cotações
        </h3>
        <span className="text-xs text-foreground-500">{cotacoes.length} total</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100/50 text-left">
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">
                Data
              </th>
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">
                Cliente
              </th>
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">
                Moeda
              </th>
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide text-right">
                Valor (R$)
              </th>
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide text-right">
                Margem
              </th>
              <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">
                Condição
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/60">
            {resultados.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground-500">Nenhuma cotação encontrada.</td></tr>}
            {resultados.map((c) => (
              <tr key={c.id} className="hover:bg-background-100/40 transition-colors">
                <td className="px-4 py-3 text-foreground-700 whitespace-nowrap font-tabular">
                  {formatDate(c.createdAt)}
                </td>
                <td className="px-4 py-3 text-foreground-950 max-w-[200px] truncate" title={c.clienteNome}>
                  {c.clienteNome}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                    c.moeda === 'BRL'
                      ? 'bg-primary-100 text-primary-700'
                      : c.moeda === 'USD'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {c.moeda}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-950 font-tabular">
                  {formatCurrency(c.totalBrl || c.total)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                    c.margemMedia >= 30
                      ? 'text-primary-600'
                      : c.margemMedia >= 25
                      ? 'text-accent-600'
                      : 'text-foreground-500'
                  }`}>
                    {!canSeeSensitive && <span className={`w-3 h-3 rounded-full ${c.margemMedia >= c.margemAlvo ? 'bg-emerald-500' : c.margemMedia >= c.margemAlvo - 5 ? 'bg-amber-400' : 'bg-red-500'}`} />}
                    {canSeeSensitive ? `${c.margemMedia.toFixed(1)}%` : c.margemMedia >= c.margemAlvo ? 'Verde' : c.margemMedia >= c.margemAlvo - 5 ? 'Amarelo' : 'Vermelho'}
                  </span>
                </td>
                <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">
                  {c.condicaoPagamento}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cotacoes.length > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200/70 px-5 py-3">
        <span className="text-xs text-foreground-600">Exibindo {inicio + 1}–{Math.min(inicio + porPagina, cotacoes.length)} de {cotacoes.length}</span>
        <div className="flex items-center gap-1.5">
          <button type="button" disabled={pagina === 1} onClick={() => setPagina((atual) => Math.max(1, atual - 1))} className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 disabled:cursor-not-allowed disabled:opacity-40">Anterior</button>
          <span className="min-w-20 text-center text-xs font-semibold text-foreground-800">{pagina} de {totalPaginas}</span>
          <button type="button" disabled={pagina === totalPaginas} onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))} className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 disabled:cursor-not-allowed disabled:opacity-40">Próxima</button>
        </div>
      </div>}
    </div>
  );
}
